import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../api/axiosInstance";
import { logoutUser } from "./authSlice";

const getErrorMessage = (err, fallback) =>
  err.response?.data?.message || fallback;

// --- Thunks -----------------------------------------------------------

// filters is optional: { candidateId, clientId, status, jdTitle,
// startDate, endDate, minAtsScore, page, limit }. page/limit absent means
// "no pagination, give me everything" (see backend/utils/pagination.js).
export const fetchOptimizations = createAsyncThunk(
  "optimizations/fetchOptimizations",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = {};
      for (const key of [
        "candidateId",
        "clientId",
        "status",
        "jdTitle",
        "startDate",
        "endDate",
        "minAtsScore",
        "page",
        "limit",
      ]) {
        if (filters[key]) params[key] = filters[key];
      }
      const res = await api.get("/optimization", { params });
      return { optimizations: res.data.optimizations, pagination: res.data.pagination || null };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to load optimizations."));
    }
  }
);

export const fetchOptimizationById = createAsyncThunk(
  "optimizations/fetchOptimizationById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/optimization/${id}`);
      return res.data.optimization;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to load optimization."));
    }
  }
);

// formData: FormData containing candidateId, clientId, jdTitle, jdUrl, jdText,
// requiredExperience, requiredSkills, preferredSkills, the eligibility
// constraint fields, and/or a jdFile.
export const createOptimization = createAsyncThunk(
  "optimizations/createOptimization",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await api.post("/optimization", formData);
      return res.data.optimization;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to create optimization."));
    }
  }
);

// decision: "ELIGIBLE" | "DISQUALIFIED" — resolves a NEEDS_REVIEW case.
export const resolveEligibility = createAsyncThunk(
  "optimizations/resolveEligibility",
  async ({ id, decision }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/optimization/${id}/eligibility-decision`, { decision });
      return res.data.optimization;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to resolve eligibility."));
    }
  }
);

// The RULE-008 checkpoint — recruiter approves/unapproves one skill for use
// in the optimized resume. skillType: "required" | "preferred".
export const updateSkillSelection = createAsyncThunk(
  "optimizations/updateSkillSelection",
  async (
    { id, skillType, name, selected, overrideJustification, overrideTargetCompany, overrideApprovedBullet },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.put(`/optimization/${id}/skill-selection`, {
        skillType,
        name,
        selected,
        overrideJustification,
        // Which of the candidate's roles the overridden skill was used in.
        overrideTargetCompany,
        // Exact AI bullet the recruiter accepted ("Use this bullet"), or null.
        overrideApprovedBullet,
      });
      return { id, requiredSkills: res.data.requiredSkills, preferredSkills: res.data.preferredSkills };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to update skill selection."));
    }
  }
);

// --- Stage 4: the 6 LLM operations (5 of the 6 live on optimizations —
// Resume Parser is on candidateSlice since it belongs to a resume version).

// LLM Op #2 — JD Parser, PREVIEW mode: no optimization id needed. Used on
// the New Optimization form to pre-fill fields from pasted JD text before
// the recruiter has even hit submit, instead of typing them by hand and
// finding out afterward that AI could have extracted the same thing.
export const previewParseJd = createAsyncThunk(
  "optimizations/previewParseJd",
  async (jdText, { rejectWithValue }) => {
    try {
      const res = await api.post("/optimization/parse-jd-preview", { jdText });
      return res.data.parsed;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to parse the job description."));
    }
  }
);

// LLM Op #2 — JD Parser. overwrite: true replaces fields the recruiter
// already typed in; default false only fills in blanks.
export const parseJd = createAsyncThunk(
  "optimizations/parseJd",
  async ({ id, overwrite = false }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/optimization/${id}/parse-jd`, { overwrite });
      return res.data.optimization;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to parse the job description."));
    }
  }
);

// LLM Op #3 — Skill Matcher.
export const matchSkills = createAsyncThunk(
  "optimizations/matchSkills",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/optimization/${id}/match-skills`);
      return { id, requiredSkills: res.data.requiredSkills, preferredSkills: res.data.preferredSkills };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to match skills."));
    }
  }
);

// LLM Op #4 — Title Alignment.
export const generateTitleAlignment = createAsyncThunk(
  "optimizations/generateTitleAlignment",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/optimization/${id}/title-alignment`);
      return { id, titleAlignment: res.data.titleAlignment };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to generate title alignment."));
    }
  }
);

// LLM Op #5 — Summary Generator. payload: { id, currentSummary?, requestedChanges? }
export const generateSummary = createAsyncThunk(
  "optimizations/generateSummary",
  async ({ id, currentSummary, requestedChanges }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/optimization/${id}/summary`, { currentSummary, requestedChanges });
      return { id, summary: res.data.summary };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to generate summary."));
    }
  }
);

// LLM Op #6 — Experience Rewriter. Requires the master resume to already
// have been AI-parsed (Resume Parser) — the backend 400s with a clear
// message if not, which the UI should surface as-is.
export const generateExperience = createAsyncThunk(
  "optimizations/generateExperience",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/optimization/${id}/experience`);
      return { id, experience: res.data.experience };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to rewrite experience."));
    }
  }
);

// Non-destructive status check for all 10 rules — safe to call anytime,
// including before Summary/Experience exist (those show PENDING). Doesn't
// touch state.selectedOptimization; the component reads the result
// directly since this is a read-only diagnostic, not a mutation.
export const fetchRuleStatus = createAsyncThunk(
  "optimizations/fetchRuleStatus",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/optimization/${id}/rule-status`);
      return res.data.rules;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to check rule status."));
    }
  }
);

// Step 15 — Final Resume + Final ATS Score. Runs the RULE-003-008
// checkpoint server-side first; a 422 means a rule was violated (surfaced
// via err.response.data.rule/message), not a generic failure.
export const generateFinalResume = createAsyncThunk(
  "optimizations/generateFinalResume",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/optimization/${id}/generate-final-resume`);
      return { id, finalResumeVersion: res.data.finalResumeVersion, atsComparison: res.data.atsComparison, optimization: res.data.optimization };
    } catch (err) {
      const rule = err.response?.data?.rule;
      const message = getErrorMessage(err, "Failed to generate the final resume.");
      return rejectWithValue(rule ? `${rule}: ${message.replace(`${rule}: `, "")}` : message);
    }
  }
);

// --- Slice --------------------------------------------------------------

const initialState = {
  optimizations: [],
  pagination: null,
  selectedOptimization: null,
  status: "idle", // idle | loading | succeeded | failed
  error: null,
};

const optimizationSlice = createSlice({
  name: "optimizations",
  initialState,
  reducers: {
    clearOptimizationError: (state) => {
      state.error = null;
    },
    clearSelectedOptimization: (state) => {
      state.selectedOptimization = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOptimizations.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchOptimizations.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.optimizations = action.payload.optimizations;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOptimizations.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(fetchOptimizationById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchOptimizationById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedOptimization = action.payload;
      })
      .addCase(fetchOptimizationById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(createOptimization.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createOptimization.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.optimizations.unshift(action.payload);
      })
      .addCase(createOptimization.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(resolveEligibility.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(resolveEligibility.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedOptimization = action.payload;
        const idx = state.optimizations.findIndex((o) => o._id === action.payload._id);
        if (idx !== -1) state.optimizations[idx] = action.payload;
      })
      .addCase(resolveEligibility.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(updateSkillSelection.fulfilled, (state, action) => {
        if (state.selectedOptimization?._id === action.payload.id) {
          state.selectedOptimization.requiredSkills = action.payload.requiredSkills;
          state.selectedOptimization.preferredSkills = action.payload.preferredSkills;
        }
      })
      .addCase(updateSkillSelection.rejected, (state, action) => {
        state.error = action.payload;
      })

      // --- Stage 4 LLM ops: each updates selectedOptimization in place.
      // Pending/rejected share one small helper-ish pattern inline since
      // each op only touches its own slice of the optimization document.
      .addCase(parseJd.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(parseJd.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedOptimization = action.payload;
        const idx = state.optimizations.findIndex((o) => o._id === action.payload._id);
        if (idx !== -1) state.optimizations[idx] = action.payload;
      })
      .addCase(parseJd.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(matchSkills.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(matchSkills.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (state.selectedOptimization?._id === action.payload.id) {
          state.selectedOptimization.requiredSkills = action.payload.requiredSkills;
          state.selectedOptimization.preferredSkills = action.payload.preferredSkills;
        }
      })
      .addCase(matchSkills.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(generateTitleAlignment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(generateTitleAlignment.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (state.selectedOptimization?._id === action.payload.id) {
          state.selectedOptimization.titleAlignment = action.payload.titleAlignment;
        }
      })
      .addCase(generateTitleAlignment.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(generateSummary.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(generateSummary.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (state.selectedOptimization?._id === action.payload.id) {
          state.selectedOptimization.summary = action.payload.summary;
        }
      })
      .addCase(generateSummary.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(generateExperience.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(generateExperience.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (state.selectedOptimization?._id === action.payload.id) {
          state.selectedOptimization.experience = action.payload.experience;
        }
      })
      .addCase(generateExperience.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(generateFinalResume.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(generateFinalResume.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (state.selectedOptimization?._id === action.payload.id) {
          state.selectedOptimization.ats.finalScore = action.payload.optimization.ats.finalScore;
          state.selectedOptimization.status = action.payload.optimization.status;
          state.selectedOptimization.finalResumeVersion = action.payload.finalResumeVersion;
          state.selectedOptimization.atsComparison = action.payload.atsComparison;
          // Notes about anything that couldn't be edited in the file.
          state.selectedOptimization.finalResumeWarnings = action.payload.optimization.finalResumeWarnings || [];
          state.selectedOptimization.ats.breakdown = action.payload.optimization.ats.breakdown;
        }
      })
      .addCase(generateFinalResume.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Wipe on logout — same reasoning as candidateSlice.
      .addCase(logoutUser.fulfilled, () => initialState);
  },
});

export const { clearOptimizationError, clearSelectedOptimization } = optimizationSlice.actions;
export default optimizationSlice.reducer;
